import { Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import {
  ApiCollectionResponse,
  ApiSimpleResponse,
  ApiSingleResponse,
} from '../interfaces/responses/api-response';
import {
  Question,
  QuestionBank,
  QuestionBankCreateDto,
} from '../interfaces/question';
import { SelectOption } from '../interfaces/select-option';
import { Theme } from '../interfaces/theme';
import { QuestionDeletionResult } from '../interfaces/question-trash';

@Injectable({
  providedIn: 'root',
})
export class QuestionService {
  private baseUrl = `${environment.BASE_URL}/questions`;

  constructor(private http: HttpClient) {}

  getQuestionsByProfessor(
    professorId: number,
  ): Observable<ApiCollectionResponse<Question>> {
    return this.http.get<ApiCollectionResponse<Question>>(
      `${this.baseUrl}/professor/${professorId}`,
    );
  }

  getQuestionById(id: number): Observable<ApiSingleResponse<QuestionBank>> {
    return this.http.get<ApiSingleResponse<QuestionBank>>(
      `${this.baseUrl}/${id}`,
    );
  }

  getQuestionWithAnswers(
    id: number,
  ): Observable<ApiSingleResponse<QuestionBank>> {
    return this.http.get<ApiSingleResponse<QuestionBank>>(
      `${this.baseUrl}/answers/${id}`,
    );
  }

  getQuestionsByThemeIds(
    themeIds: number[],
  ): Observable<ApiCollectionResponse<QuestionBank>> {
    return this.http.post<ApiCollectionResponse<QuestionBank>>(
      `${this.baseUrl}/themes`,
      themeIds,
    );
  }

  create(
    payload: QuestionBankCreateDto,
  ): Observable<ApiSingleResponse<QuestionBank>> {
    return this.http.post<ApiSingleResponse<QuestionBank>>(
      `${this.baseUrl}`,
      payload,
    );
  }

  /**
   * Delete a Question
   * @param id number
   * @returns Observable of API simple response with status message
   */
  delete(questionId: number): Observable<ApiSimpleResponse> {
    return this.http.delete<ApiSimpleResponse>(`${this.baseUrl}/${questionId}`);
  }

  /**
   * Met des questions à la corbeille. Réversible : rien n'est détruit.
   *
   * <p>Une question à laquelle un élève a répondu est refusée — la supprimer
   * rendrait sa copie illisible. Le refus est rendu question par question, et
   * les tests qui perdent des questions sont signalés sans être bloquants.</p>
   */
  mettreALaCorbeille(ids: number[]): Observable<ApiSingleResponse<QuestionDeletionResult>> {
    return this.http.delete<ApiSingleResponse<QuestionDeletionResult>>(this.baseUrl, {
      body: { ids },
    });
  }

  /** Ce que la corbeille contient. Administration seule. */
  corbeille(): Observable<ApiCollectionResponse<QuestionBank>> {
    return this.http.get<ApiCollectionResponse<QuestionBank>>(`${this.baseUrl}/trash`);
  }

  /** Remet en service. Administration seule. */
  restaurer(ids: number[]): Observable<ApiCollectionResponse<number>> {
    return this.http.post<ApiCollectionResponse<number>>(`${this.baseUrl}/trash/restore`, { ids });
  }

  /**
   * Détruit définitivement, depuis la corbeille seulement.
   *
   * <p>Sans identifiants, la corbeille entière. Une question en service n'est
   * jamais atteinte : elle y passe d'abord, où elle reste récupérable.</p>
   */
  viderLaCorbeille(ids?: number[]): Observable<ApiSingleResponse<number>> {
    return this.http.delete<ApiSingleResponse<number>>(`${this.baseUrl}/trash`, {
      body: ids?.length ? { ids } : undefined,
    });
  }

  updateQuestion(
    id: number,
    payload: any,
  ): Observable<ApiSingleResponse<QuestionBank>> {
    return this.http.put<ApiSingleResponse<QuestionBank>>(
      `${this.baseUrl}/${id}`,
      payload,
    );
  }
}
